/**
 * 工事費用の詳細ページの金額を入れる（sections/aicon-installation-fee.liquid）。
 *
 * ★ テーマに金額を持たない ★
 *   GET /apps/aicon/pricing が返す `data.values`（意味の決まったキーの平たい表）から
 *   `data-kfee-price` のキーで引いて入れる。
 *
 * ★ ラベルで引かない ★
 *   このページは固定レイアウトの決まった位置に金額が入る。groups をラベル文字列で
 *   検索すると、文字を1つ直しただけで**空欄**になり 0 と違って気づけない。
 *
 * ★ 落ちたときは「—」のまま ★
 *   存在しない金額を出すより、出ないほうが害が小さい（料金表と同じ判断）。
 *   ページの本文・写真・説明は金額に依存しないので、そのまま読める。
 */
(function () {
  'use strict';
  // メーカーページは複数のセクション（コンセント表・工事費の前半・後半）がこのファイルを読み込む。
  // 2回目以降は何もしない（1回の init がページの全部の data-kfee-root を埋める。料金の取得を重ねない）
  if (window.aiconKfeeReady) return;
  window.aiconKfeeReady = true;

  /** Shopify のロケール付きルート（/ja/ など）を踏む。ハードコードすると多言語で落ちる */
  function routeRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /** "a.b.c" でネストを引く。途中で欠けたら undefined（例外にしない） */
  function pick(obj, path) {
    return path.split('.').reduce(function (o, k) {
      return o === null || o === undefined ? undefined : o[k];
    }, obj);
  }

  var yen = function (n) { return n.toLocaleString('ja-JP') + '円'; };

  /**
   * 表示の形。デザイン原本の書き方をそのまま保つ
   * （「N円/m」「N円引き」「交換標準工事費+N円」「+N円」の4種と、単位なしの数・桁区切りだけの数）。
   * ★ ここに具体の額を書かないこと ★ 料金改定でコメントだけ嘘になる。
   */
  var FORMAT = {
    yen: yen,
    perM: function (n) { return yen(n) + '/m'; },
    discount: function (n) { return yen(n) + '引き'; },
    plusYen: function (n) { return '+' + yen(n); },
    addon: function (n) { return '交換標準工事費+' + yen(n); },
    num: function (n) { return String(n); },
    comma: function (n) { return n.toLocaleString('ja-JP'); },
  };

  function fill(root, values) {
    var slots = root.querySelectorAll('[data-kfee-price]');
    var missing = [];
    for (var i = 0; i < slots.length; i++) {
      var el = slots[i];
      var key = el.getAttribute('data-kfee-price');
      var raw = pick(values, key);
      if (typeof raw !== 'number' || !isFinite(raw)) { missing.push(key); continue; }
      var fmt = FORMAT[el.getAttribute('data-kfee-fmt')] || FORMAT.yen;
      el.textContent = fmt(raw);
    }
    if (missing.length) {
      // ★ 黙って空欄にしない ★ キーの綴り違い・API の項目落ちを気づけるようにする
      console.warn('[kfee] 金額を入れられないキーがあります:', missing);
    }
    return missing.length === 0;
  }

  async function load(root) {
    try {
      var res = await fetch(routeRoot() + 'apps/aicon/pricing', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('status=' + res.status);
      var json = await res.json();
      var values = json && json.data && json.data.values;
      if (!values) throw new Error('values が空');
      root.setAttribute('data-kfee-status', fill(root, values) ? 'ready' : 'partial');
    } catch (err) {
      // 金額以外は読めるページなので、本文は消さない
      root.setAttribute('data-kfee-status', 'failed');
      console.error('[kfee] 料金の取得に失敗しました:', err);
    }
  }

  /**
   * セクションナビの現在地ハイライト（デザイン原本の script をそのまま移植）。
   * 金額の取得と独立させる。取得が失敗してもナビは動く。
   */
  function wireNav(root) {
    var nav = root.querySelector('.kfee-secnav');
    if (!nav) return;
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    var secs = links
      .map(function (a) { return root.querySelector(a.getAttribute('href')); })
      .filter(Boolean);
    if (!secs.length) return;

    /**
     * 画面の上に貼り付いているものの下端（＝ナビを置くべき位置）。
     *
     * ★ 高さの足し算も、CSS 変数も当てにしない（2026-09-03・3度目）★
     *   1度目: `top:0` … ヘッダーとカレンダーバーに乗り上げた。
     *   2度目: `--header-height + --header-group-height` … **ヘッダーを二重に数えた**。
     *          theme.liquid の計測が除外するのは `header-component` 自身だが、
     *          `#header-group` の直下にいるのはそれを包む `.header-section`。
     *          ∴ `--header-group-height` にはヘッダーの高さがすでに入っている。
     *          結果、ナビがヘッダー1つ分だけ下に浮いた（日高さん報告・実機）。
     *
     * ∴ 3度目は**画面に貼り付いている要素の下端をそのまま測る**。
     *   - `position: sticky` の子だけを数える（スクロールで流れる帯は数えない）
     *   - 高さ 0 の子（`<script>` や非表示のバー）は無視する
     *   - スクロールのたびに測り直すので、貼り付き方が変わっても自分で直る
     *   テーマ内部の作り・CSS 変数の意味に一切依存しない。
     */
    /**
     * 貼り付く子の一覧。`position` の判定は毎フレームやらない。
     *
     * ★ スクロール中に getComputedStyle を呼ばない ★
     *   スクロールのたびに全子要素のスタイルを解決するとレイアウトを余計に走らせる。
     *   貼り付くかどうかは滅多に変わらないので覚えておき、
     *   読み込み完了・リサイズ・子の高さ変化のときだけ取り直す。
     */
    var stickyKidsCache = null;
    function stickyKids(g) {
      if (stickyKidsCache) return stickyKidsCache;
      var out = [];
      for (var i = 0; i < g.children.length; i++) {
        var el = g.children[i];
        // 高さ 0 の子（`<script>` や出していないバー）は数えない
        if (el.getBoundingClientRect().height === 0) continue;
        if (getComputedStyle(el).position !== 'sticky') continue;
        out.push(el);
      }
      stickyKidsCache = out;
      return out;
    }

    function stackBottom() {
      var g = document.getElementById('header-group');
      if (!g) {
        // 万一 header-group が無いテンプレートでも、ナビが本文に埋もれないようにする
        var cs = getComputedStyle(document.body);
        return parseFloat(cs.getPropertyValue('--header-group-height')) || 104;
      }
      // ★ g 自身は測らない ★ header.liquid が `#header-group{display:contents}` に
      //   しており、ボックスを持たない（だから子がページ全体で貼り付ける）。
      //   子（.header-section / .aicon-calbar-wrapper）を1つずつ見る。
      var bottom = 0;
      var kids = stickyKids(g);
      for (var i = 0; i < kids.length; i++) {
        var r = kids[i].getBoundingClientRect();
        if (r.bottom > bottom) bottom = r.bottom;
      }
      if (bottom > 0) return Math.round(bottom);
      // 貼り付く子が1つも無いページ（バーを出していない等）は CSS 変数に寄せる
      return Math.round(parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 104);
    }

    /** タブの切り替え判定に使う、隠れている高さ */
    function stickyOffset() {
      return stackBottom() + nav.offsetHeight;
    }

    /**
     * ナビの貼り付き位置と、見出しの着地点を CSS へ渡す。
     * `--kfee-secnav-height` はナビ自身の高さ（CSS からは測れないため）。
     */
    var lastStack = -1;
    var lastNavH = -1;
    function publishMetrics() {
      var stack = stackBottom();
      var navH = nav.offsetHeight;
      if (stack !== lastStack) {
        lastStack = stack;
        document.body.style.setProperty('--kfee-stack-height', stack + 'px');
      }
      if (navH !== lastNavH) {
        lastNavH = navH;
        document.body.style.setProperty('--kfee-secnav-height', navH + 'px');
      }
    }

    var last = '';
    function update() {
      var y = window.scrollY + stickyOffset() + 24;
      var cur = secs[0];
      for (var i = 0; i < secs.length; i++) if (secs[i].offsetTop <= y) cur = secs[i];
      if (!cur || cur.id === last) return;
      last = cur.id;
      links.forEach(function (a) {
        var on = a.getAttribute('href') === '#' + cur.id;
        a.classList.toggle('kfee-secnav-on', on);
        if (on) nav.scrollTo({ left: a.offsetLeft - nav.offsetWidth / 2 + a.offsetWidth / 2, behavior: 'smooth' });
      });
    }
    // ★ スクロール中も測り直す ★
    //   貼り付き方が変わっても自分で直るようにする。読むのは矩形2〜3個だけだが、
    //   1フレームに1回へ間引いてスクロールを重くしない。
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        publishMetrics();
        update();
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      stickyKidsCache = null;
      publishMetrics();
      update();
    }, { passive: true });

    // ★ 高さが後から変わるものを見張る ★
    //   カレンダーバーは中身を読み込んでから高さが決まり、ロゴも画像の読み込みで伸びる。
    //   初回に測っただけだと、そのぶんナビの位置がずれたまま残る。
    //
    // ★ 親ではなく「子」を見る（2026-09-03）★
    //   `#header-group` は `display:contents` でボックスを持たないため、
    //   ResizeObserver に渡しても**一度も発火しない**（実測で確認）。
    //   最初これを親に掛けており、スクロールするまでズレたままだった。
    var group = document.getElementById('header-group');
    if (group && typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () {
        stickyKidsCache = null; // 高さが変われば数え直す（0 → 表示、の切り替わりを拾う）
        publishMetrics();
        update();
      });
      for (var k = 0; k < group.children.length; k++) ro.observe(group.children[k]);
    }
    window.addEventListener('load', function () {
      stickyKidsCache = null;
      publishMetrics();
      update();
    });

    publishMetrics();
    update();
  }

  /**
   * 画面に入ってから動画を読み込む（2026-09-09）。
   *
   * ★ なぜ要るか ★
   *   テープ巻きの動画は「5つの条件」の5枚目にあり、ページのかなり下にある。
   *   <video src> を直に書くと、そこまで来ない人にも全部落ちてくる。
   *   src を後から入れることで、見た人だけが読み込む形にする。
   *   ∴ 画質を上げてファイルを大きくしても、初回表示の重さは増えない。
   *
   * ★ 1度入れたら二度と入れない ★
   *   src を入れた時点で属性を消し unobserve する。交差は出入りで何度も起きるため、
   *   毎回入れると再生位置が先頭に戻り続ける。
   *
   * ★ IntersectionObserver が無い環境では即座に入れる ★
   *   読み込まないまま放置すると poster のまま動かない。
   *   遅延は最適化であって機能ではないので、対応していなければ素直に読み込む。
   *
   * ★ play() の失敗は無視する ★
   *   自動再生は muted でも環境によって拒否される。拒否されても poster は出ており
   *   利用者は再生操作ができる。ここで例外を投げると以降の初期化が止まる。
   */
  function wireLazyVideo(root) {
    var videos = root.querySelectorAll('video[data-kfee-video]');
    if (videos.length === 0) return;

    var start = function (video) {
      var src = video.getAttribute('data-kfee-video');
      if (!src) return;
      video.removeAttribute('data-kfee-video');
      video.src = src;
      video.load();
      var played = video.play();
      if (played && typeof played.catch === 'function') played.catch(function () {});
    };

    if (typeof window.IntersectionObserver !== 'function') {
      for (var i = 0; i < videos.length; i++) start(videos[i]);
      return;
    }

    // rootMargin で画面に入る少し前から取りに行く（到達時に黒画面を出さない）
    var io = new window.IntersectionObserver(
      function (entries) {
        for (var j = 0; j < entries.length; j++) {
          if (!entries[j].isIntersecting) continue;
          io.unobserve(entries[j].target);
          start(entries[j].target);
        }
      },
      { rootMargin: '200px 0px' }
    );
    for (var k = 0; k < videos.length; k++) io.observe(videos[k]);
  }

  function init() {
    var roots = document.querySelectorAll('[data-kfee-root]');
    for (var i = 0; i < roots.length; i++) {
      load(roots[i]);
      wireNav(roots[i]);
      wireLazyVideo(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  // テーマエディタでセクションを差し替えたときにも入れ直す
  document.addEventListener('shopify:section:load', init);
})();
